import { addItemToBasket, BASKET_TYPES, removeItemFromBasket, updateBasketItem } from 'containers/OrderDetails/basketSlice'
import {
  DAY_MINUTES, ShortTermCalendarData, ShortTermConfig,
  durationLabel, estimatePrice, fitDuration, formatWall, getDefaultDuration, getDefaultWindow, getDurationOptions,
  getFreeStarts, getHorizon, isShortTermCapable, isWindowFree, nowWall, parseBusy, parseWall, wallDay, wallTime
} from 'utils/shortTerm'
import RentalWindowSummary from 'containers/OrderDetails/Calendar/ShortTerm/RentalWindowSummary'
import { DatePicker, TimePicker } from 'components/pickers/DateTimePicker'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { selectUserId } from 'containers/Login/loginSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import { LOCATION_STATES } from 'utils/constants'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import { dateToYMDformat } from 'utils/date'
import { displayPrice } from 'utils/string'
import Popup from 'components/popups/Popup'
import Loading from 'components/Loading'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'
import { v7 } from 'uuid'

interface RentalProductPopupProps {
  product: shop.RentalProduct
  /** pozycja rent tego obiektu, jeśli już jest w koszyku — popup edytuje jej termin */
  basketItem?: basic.Form
  /** skąd otwarto sklep: identifier pozycji koszyka albo wynajęty magazyn (lokalnie, do list w sklepie i OrderDetails) */
  cartId?: string
  warehouseId?: string
  /** duże zdjęcie tylko, gdy w sklepie jest jedno auto do wynajmu (przy kilku zdjęcia są na kaflach listy) */
  showPhoto?: boolean
}

/** "Toyota Yaris 2023" z danych pojazdu, inaczej nazwa produktu */
export const getRentalProductName = ({ vehicle, name }: shop.RentalProduct) =>
  [vehicle?.brand, vehicle?.model, vehicle?.yearOfProduction].filter(Boolean).join(' ') || name

/**
 * Wynajem krótkoterminowy ze sklepu (config.rentalUpsellEnabled). Przy otwarciu pobieramy checkPrice samego obiektu
 * (świeża zajętość calendar.busy + cennik), a wybrane okno dodajemy do koszyka jako zwykłą pozycję rent.
 */
const RentalProductPopup: React.FC<RentalProductPopupProps> = ({ product, basketItem, cartId, warehouseId, showPhoto = false }) => {
  const userId = useReduxSelector(selectUserId)
  const tenant = useReduxSelector(state => (state as any).basket?.tenant)

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [shortTerm, setShortTerm] = useState<ShortTermConfig>()
  const [calendar, setCalendar] = useState<ShortTermCalendarData>()
  const [startDateTime, setStartDateTime] = useState<string | undefined>(basketItem?.startDateTime)
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>(basketItem?.durationMinutes)

  const dispatch = useReduxDispatch()
  const { goBack, replace } = useHistory()
  const { t, lang } = useTranslation('shop.rental')
  const { t: tShop } = useTranslation('shop')

  const load = useCallback(async () => {
    setStatus('loading')
    const tmpId = v7()
    try {
      const response = await POST(urls.checkPrice, {
        body: {
          userID: tenant?.user?.id || userId || '',
          companyID: tenant?.company?.id,
          objects: [{ object: 'Warehouse', id: product.id, placeID: product.placeId, rentableType: product.rentableType, cartId: tmpId }]
        }
      }, false)
      const data = (response?.objects || response)?.[tmpId]
      const config = data?.shortTerm || product.shortTerm
      if (!isShortTermCapable(config)) {
        setShortTerm(undefined)
        setStatus('ready')
        return
      }
      setShortTerm(config)
      setCalendar(data?.calendar)
      if (!basketItem?.startDateTime) {
        const window = getDefaultWindow(config, data?.calendar)
        setStartDateTime(window?.startDateTime)
        setDurationMinutes(window?.durationMinutes)
      }
      setStatus('ready')
    } catch (error) {
      console.error(error)
      setStatus('error')
    }
  }, [basketItem?.startDateTime, product.id, product.placeId, product.rentableType, product.shortTerm, tenant?.company?.id, tenant?.user?.id, userId])

  useEffect(() => {
    load()
    // tylko przy otwarciu popupu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const estimate = shortTerm && durationMinutes ? estimatePrice(shortTerm, durationMinutes) : undefined
  const name = getRentalProductName(product)

  // --- wybór terminu: data (dni bez wolnego startu wyłączone) + godzina (tylko wolne starty) + lista długości z cennika
  const busy = useMemo(() => parseBusy(calendar?.busy), [calendar?.busy])
  const horizon = useMemo(() => getHorizon(calendar, nowWall()), [calendar])
  const freeStarts = useMemo(
    () => shortTerm ? getFreeStarts(shortTerm, busy, horizon.from, horizon.to) : [],
    [busy, horizon.from, horizon.to, shortTerm]
  )
  const freeDays = useMemo(() => new Set(freeStarts.map(s => formatWall(wallDay(s)).slice(0, 10))), [freeStarts])
  const durationOptions = useMemo(() => shortTerm ? getDurationOptions(shortTerm) : [], [shortTerm])

  const start = parseWall(startDateTime)
  const selectedDate = startDateTime?.slice(0, 10)
  const timesForDate = freeStarts
    .filter(s => formatWall(s).slice(0, 10) === selectedDate)
    .map(s => wallTime(s))
  const lastDate = formatWall(horizon.to).slice(0, 10)
  const today = formatWall(horizon.from).slice(0, 10)

  const setWindow = (s: number, wanted = durationMinutes || (shortTerm ? getDefaultDuration(shortTerm) : 60)) => {
    if (!shortTerm) return
    setStartDateTime(formatWall(s))
    setDurationMinutes(fitDuration(shortTerm, busy, s, wanted))
  }

  const selectDate = (ymd: string) => {
    const day = parseWall(ymd)
    if (day === undefined) return
    const first = freeStarts.find(s => s >= day && s < day + DAY_MINUTES)
    if (first !== undefined) setWindow(first)
  }

  const fits = (minutes: number) => start !== undefined && !!shortTerm
    && (!shortTerm.maxMinutes || minutes <= shortTerm.maxMinutes)
    && isWindowFree(busy, start, start + minutes)

  const submit = () => {
    if (!startDateTime || !durationMinutes) return
    if (basketItem) {
      dispatch(updateBasketItem({
        identifier: basketItem.identifier,
        values: { ok: true, startDateTime, durationMinutes, endDateTime: undefined }
      }))
    } else {
      const identifier = v7()
      dispatch(addItemToBasket({
        identifier,
        type: BASKET_TYPES.rent,
        object: 'Warehouse',
        id: product.id,
        placeId: product.placeId,
        rentableType: product.rentableType,
        name,
        image: product.warehouseTransparentPhoto || product.warehousePhoto,
        isShortTerm: true,
        startDateTime,
        durationMinutes,
        // tylko lokalnie: skąd dodano (listy w sklepie i OrderDetails), nie trafia do API
        rentalUpsellFor: cartId || warehouseId || 'shop'
      }))
      replace(window.location.pathname, LOCATION_STATES.popup({ type: 'basket' }))
      return
    }
    goBack()
  }

  return (
    <Popup darkBg>
      <div className='shopCarRental__popup shopRental__popup'>
        <div className='shopCarRental__header'>
          <div className='shopCarRental__title'>{name}</div>
          <div className='shopCarRental__dimensions'>
            {product.vehicle?.registrationNumber && <><span>{t('registration')}:</span> {product.vehicle.registrationNumber} · </>}
            {product.typeLabel}
            {product.vehicle?.cargoDimensions && <> · <span>{t('cargo')}:</span> {product.vehicle.cargoDimensions}</>}
            {product.samePlace === false && product.placeName && <> · {t('pickupAt').replace('{place}', product.placeName)}</>}
          </div>
        </div>

        {showPhoto && product.warehousePhoto && <div className='shopCarRental__photo'>
          <img src={product.warehousePhoto} alt={name} />
        </div>}

        {product.description && <p className='shopRental__desc'>{product.description}</p>}

        {status === 'loading'
          ? <div className='shopRental__loading'><Loading /></div>
          : status === 'error'
            ? <div className='shopRental__message'>
              <span>{t('loadError')}</span>
              <ArrowBtn
                showArrow={false}
                className='--secondary'
                text={t('retry')}
                action={load}
              />
            </div>
            : !shortTerm
              ? <div className='shopRental__message'>{t('notCapable')}</div>
              : freeStarts.length === 0
                ? <div className='shopRental__message'>{t('noAvailability')}</div>
                : <>
                  <p className='shopCarRental__subtitle'>{tShop('whenDoYouWantToPickUpTheCar')}</p>
                  <div className='shopCarRental__dateTime'>
                    <DatePicker
                      isPopup
                      name='rental-date'
                      value={selectedDate}
                      minDate={today}
                      maxDate={lastDate}
                      tileDisabled={({ date, view }) => view === 'month' && !freeDays.has(dateToYMDformat(date))}
                      setValue={selectDate}
                    />
                    <TimePicker
                      isPopup
                      name='rental-time'
                      value={start !== undefined ? wallTime(start) : undefined}
                      allowedTimes={timesForDate}
                      disabled={!selectedDate || timesForDate.length === 0}
                      setValue={time => {
                        const s = parseWall(`${selectedDate} ${time}`)
                        if (s !== undefined) setWindow(s)
                      }}
                    />
                  </div>

                  <p className='shopCarRental__subtitle'>{tShop('selectRentalPeriod')}</p>
                  <div className='shopCarRental__periods'>
                    {durationOptions.map(minutes => {
                      const price = shortTerm ? estimatePrice(shortTerm, minutes) : undefined
                      const isSelected = minutes === durationMinutes
                      const isAvailable = fits(minutes)
                      return <div
                        key={minutes}
                        className={`shopCarRental__period${isSelected ? ' --selected' : ''}${isAvailable ? '' : ' --disabled'}`}
                        onClick={() => {
                          if (isAvailable) setDurationMinutes(minutes)
                        }}>
                        <span>{durationLabel(minutes, lang)}{price ? ` (${displayPrice(price.gross)})` : ''}</span>
                        <div className='shopCarRental__period__circle center'>
                          <i className='icon icon--check' />
                        </div>
                      </div>
                    })}
                  </div>

                  {durationMinutes && <div className='shortTermCalendar'>
                    <RentalWindowSummary
                      startDateTime={startDateTime}
                      durationMinutes={durationMinutes}
                      pricing={estimate}
                    />
                  </div>}
                </>}

        <div className='shopCarRental__buttons'>
          <ArrowBtn
            enlarged
            showArrow={false}
            disabled={status !== 'ready' || !startDateTime || !durationMinutes}
            text={basketItem
              ? t('save')
              : estimate ? t('addToCartFor').replace('{price}', displayPrice(estimate.gross)) : t('addToCart')}
            action={submit}
          />
          {basketItem
            ? <BackBtn
              text={t('remove')}
              action={() => {
                dispatch(removeItemFromBasket({ removeIdentifiers: [basketItem.identifier] }))
                goBack()
              }}
            />
            : <BackBtn />}
        </div>
      </div>
    </Popup>
  )
}

export default RentalProductPopup
